/**
 * Typed runtime accessor for the bundled changelog.
 *
 * The changelog artifact (`src/generated/changelog.json`) is produced at build
 * time from the repository root `CHANGELOG.md` and bundled into the SPA. This
 * module imports that static artifact directly and exposes it as a typed
 * `Changelog`, so the application never performs a runtime network fetch for
 * changelog content (Req 3.3, 3.4).
 */
import { compareSemver } from './semver';
import changelogData from '../generated/changelog.json';

/** A single user-facing change within a Version. */
export interface ChangelogEntry {
  /** User-facing subject, trimmed, with any `**scope:**` marker/hash removed. */
  text: string;
  /** Optional conventional-commit scope, e.g. "frontend". */
  scope?: string;
  /** Optional short commit hash, e.g. "cf646a3". */
  commitHash?: string;
  /** Optional commit URL. */
  commitUrl?: string;
}

/** A single released version and its user-facing changes. */
export interface Version {
  /** Semantic version, `x.y.z`. */
  version: string;
  /** Release date, `YYYY-MM-DD`. */
  date: string;
  /** Features section entries, in source order. */
  features: ChangelogEntry[];
  /** Bug Fixes section entries, in source order. */
  fixes: ChangelogEntry[];
}

/** The complete structured collection of released versions. */
export interface Changelog {
  versions: Version[];
}

/**
 * The bundled changelog, typed. Sourced from the build-time artifact — no
 * runtime fetch (Req 3.3, 3.4). The JSON shape matches the `Changelog` model.
 */
export const changelog: Changelog = changelogData as Changelog;

/**
 * Return the changelog versions sorted greatest-first by semantic version
 * ordering (newest version first). Does not mutate the source array (Req 4.2).
 */
export function getVersionsNewestFirst(): Version[] {
  return [...changelog.versions].sort((a, b) => compareSemver(b.version, a.version));
}

/**
 * Return the greatest Version by semantic version ordering (Latest_Version),
 * or `null` when there are no versions (Req 6.1).
 */
export function getLatestVersion(): Version | null {
  const versions = changelog.versions;
  if (versions.length === 0) {
    return null;
  }

  return versions.reduce((latest, candidate) =>
    compareSemver(candidate.version, latest.version) > 0 ? candidate : latest,
  );
}
